import React from 'react';
import styled from 'styled-components';
import Navigation from '../../../components/portal/Navigation';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import { apiService } from '../../../services/api';
import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';

import {
  Payments as PaymentsIcon,
} from '@mui/icons-material';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;
const ContentWrapper = styled.div`
  position: relative; z-index: 3; max-width: 1200px; margin: 0 auto; padding: var(--desktop-page-padding-x, 2rem); padding-top: 6rem;
  @media (max-width: 768px) {
    padding: 1rem var(--mobile-page-padding-x);
    padding-top: 5rem;
  }
`;
const ContentCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.06);
  margin-bottom: 1rem; 
  padding: 1.5rem;
`;
const ListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px dashed rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding: 0.75rem;
    gap: 0.75rem;
  }
`;

const ItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  
  .icon { 
    font-size: 1.1rem; 
    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }
  
  .details { display: flex; flex-direction: column; }
  .title { 
    font-weight: 800; 
    font-size: 0.95rem;
    color: var(--heading, #222);
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
  
  .meta { 
    font-size: 0.8rem; 
    color: var(--text-light);
    
    .time-part {
      @media (max-width: 768px) {
        display: none;
      }
    }
  }
`;

const ItemRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 768px) {
    justify-content: space-between;
    width: 100%;
    padding-top: 0.4rem;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
  }

  .amount {
    font-weight: 800;
    font-size: 1rem;
    color: #ef4444;

    @media (max-width: 768px) {
      font-size: 1.1rem;
    }

    &.history {
      color: inherit;
    }
  }

  .actions {
    display: flex;
    gap: 0.5rem;

    @media (max-width: 768px) {
      flex: 1;
      justify-content: flex-end;
    }
  }
`;
const Badge = styled.span<{ color?: 'green'|'yellow'|'red'|'blue' }>`
  padding:.2rem .5rem; border-radius:999px; font-weight:800; font-size:.75rem; color:#fff;
  background: ${p => p.color==='green' ? 'linear-gradient(135deg,#11998e,#38ef7d)'
                          : p.color==='yellow' ? 'linear-gradient(135deg,#f6d365,#fda085)'
                          : p.color==='red' ? 'linear-gradient(135deg,#fc4a1a,#f7b733)'
                          : 'linear-gradient(135deg,#667eea,#764ba2)'};
`;

const SummaryContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    justify-content: stretch;
  }
`;

const SummaryCard = styled.div`
  background: var(--card-bg, rgba(255,255,255,0.95));
  backdrop-filter: blur(20px);
  padding: 1.25rem 1.5rem;
  border-radius: 16px;
  display: flex;
  align-items: center;
  gap: 1rem;
  border: 1px solid var(--card-border, rgba(255,255,255,0.2));
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  min-width: 280px;

  @media (max-width: 768px) {
    flex: 1;
    min-width: unset;
  }

  .icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: #fff7ed;
    color: #f59e0b;
  }

  .content {
    display: flex;
    flex-direction: column;
    
    .label {
      font-size: 0.75rem;
      font-weight: 700;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .value {
      font-size: 1.25rem;
      font-weight: 900;
      color: #1e293b;
    }
  }
`;

const TeacherPayments: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string|null>(null);
  const [payments, setPayments] = React.useState<any[]>([]);
  const [outstanding, setOutstanding] = React.useState<any[]>([]);
  const [actionLoading, setActionLoading] = React.useState<number|null>(null);
  const [successMsg, setSuccessMsg] = React.useState<string|null>(null);

  const handleVerify = async (paymentId: number, status: string) => {
    try {
      setActionLoading(paymentId);
      const qs = new URLSearchParams();
      qs.set('status', status.toUpperCase());
      await apiService.post(API_ENDPOINTS.PAYMENTS.VERIFY(paymentId) + `?${qs.toString()}`);
      setSuccessMsg(status === 'VERIFIED' ? (t('portal.teacher.payments.verified', 'Payment verified and bill recalculated') as string) : (t('portal.teacher.payments.rejected', 'Payment rejected') as string));
      setTimeout(() => setSuccessMsg(null), 3000);
      load();
    } catch (e: any) {
      alert(e?.message || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const load = React.useCallback( async () => {
    setLoading(true); setError(null);
    try {
      const [paymentsResp, outstandingResp] = await Promise.all([
        apiService.get<any>(API_ENDPOINTS.PAYMENTS.TEACHER_STUDENTS),
        apiService.get<any[]>(API_ENDPOINTS.PAYMENTS.TEACHER_OUTSTANDING)
      ]);
      
      const pList = Array.isArray((paymentsResp as any)?.data) ? (paymentsResp as any).data : (Array.isArray(paymentsResp) ? paymentsResp : []);
      setPayments(pList);
      
      const oList = Array.isArray(outstandingResp) ? outstandingResp : [];
      setOutstanding(oList);
    } catch (e:any) {
      setError(e?.message || (t('portal.teacher.payments.error_load', 'Failed to load payments') as string));
    } finally { setLoading(false); }
  }, [t]);

  const sendBill = async (studentId: number) => {
    setActionLoading(studentId);
    try {
      const resp: any = await apiService.post(`/api/teacher/students/${studentId}/send-bill`, {});
      setSuccessMsg(resp?.message || (t('portal.teacher.payments.success_send_bill', 'Bill sent successfully') as string));
      setTimeout(() => setSuccessMsg(null), 3000);
      load(); // refresh to get updated amounts if changed
    } catch (e: any) {
      alert(e?.message || (t('portal.teacher.payments.error_send_bill', 'Failed to send bill') as string));
    } finally {
      setActionLoading(null);
    }
  };

  React.useEffect(()=>{ load(); }, [load]);

  const totalOutstanding = React.useMemo(() => {
    return outstanding.reduce((acc, curr) => acc + (curr.amount || 0), 0);
  }, [outstanding]);

  const formatCurrency = (amount: number, currency: string) => new Intl.NumberFormat(i18n.language === 'nl' ? 'nl-NL' : (i18n.language === 'tr' ? 'tr-TR' : 'en-GB'), { style: 'currency', currency: currency || 'EUR' }).format(amount || 0);
  const titleFor = (p:any) => {
    const name = p?.student?.firstName || p?.student?.lastName ? `${p.student.firstName ?? ''} ${p.student.lastName ?? ''}`.trim() : (p.student?.username || p.student?.email || (t('portal.teacher.payments.student_fallback', 'Student') as string));
    return name;
  };

  const statusColor = (s: string): 'green'|'yellow'|'red'|'blue' => {
    const v = String(s||'').toUpperCase();
    if (v==='VERIFIED') return 'green';
    if (v==='PENDING') return 'yellow';
    if (v==='REJECTED') return 'red';
    return 'blue';
  };

  return (
    <PageWrapper>
      <Navigation />
      <ContentWrapper>
        {successMsg && (
          <div style={{ background: '#10b981', color: '#fff', padding: '1rem', borderRadius: '12px', marginBottom: '1rem', fontWeight: 800 }}>
            ✅ {successMsg}
          </div>
        )}

        <SummaryContainer>
          <SummaryCard>
            <div className="icon-wrapper">
              <PaymentsIcon />
            </div>
            <div className="content">
              <span className="label">{t('portal.teacher.payments.total_outstanding', 'Total Outstanding')}</span>
              <span className="value">{formatCurrency(totalOutstanding, 'EUR')}</span>
            </div>
          </SummaryCard>
        </SummaryContainer>

        <ContentCard>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:'.5rem', flexWrap:'wrap', marginBottom:'1.5rem'}}>
            <h3 style={{margin:0}}>{t('portal.teacher.payments.title_outstanding', 'Openstaande betalingen')} ({outstanding.length})</h3>
            <button onClick={load} style={{ border:0, borderRadius:10, padding:'.45rem .75rem', fontWeight:800, color:'#fff', background:'linear-gradient(135deg,#667eea,#764ba2)', cursor: 'pointer' }}>{t('portal.common.refresh', 'Refresh')}</button>
          </div>
          
          {loading ? (
            <div style={{ padding:'1rem', color:'#555' }}>{t('portal.teacher.payments.loading_outstanding', 'Loading…')}</div>
          ) : outstanding.length === 0 ? (
            <div style={{ padding:'1.25rem', textAlign:'center', color:'#666' }}>
              <div style={{ fontSize:'2rem', opacity:.35, marginBottom:8 }}>✨</div>
              {t('portal.teacher.payments.no_outstanding', 'No outstanding payments.')}
            </div>
          ) : (
            <div>
              {outstanding.map((o: any) => (
                <ListItem key={o.studentId}>
                  <ItemLeft>
                    <span className="icon">👤</span>
                    <div className="details">
                      <div className="title">{o.studentFirstName} {o.studentLastName}</div>
                      <div className="meta">
                        @{o.studentUsername} • {t('portal.teacher.payments.last_update', 'Last update')}: {new Date(o.updatedAt).toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : (i18n.language === 'tr' ? 'tr-TR' : 'en-GB'))}
                      </div>
                    </div>
                  </ItemLeft>
                  <ItemRight>
                    <div className="amount">{formatCurrency(o.amount, o.currency)}</div>
                    <div className="actions">
                      <button 
                        onClick={() => navigate(`/portal/teacher/student/${o.studentId}`)}
                        style={{ 
                          border: '2px solid #667eea', 
                          borderRadius: 10, 
                          padding: '.4rem .8rem', 
                          fontWeight: 800, 
                          color: '#667eea', 
                          background: 'transparent',
                          cursor: 'pointer',
                          fontSize: '.85rem'
                        }}
                      >
                        {t('portal.common.details', 'Details')}
                      </button>
                      <button 
                        disabled={actionLoading === o.studentId}
                        onClick={() => sendBill(o.studentId)}
                        style={{ 
                          border: 'none', 
                          borderRadius: 10, 
                          padding: '.4rem .8rem', 
                          fontWeight: 800, 
                          color: '#fff', 
                          background: 'linear-gradient(135deg,#667eea,#764ba2)',
                          cursor: 'pointer',
                          fontSize: '.85rem'
                        }}
                      >
                        {actionLoading === o.studentId ? (t('portal.teacher.payments.sending', 'Sending...') as string) : (t('portal.teacher.payments.send_bill', 'Send Bill') as string)}
                      </button>
                    </div>
                  </ItemRight>
                </ListItem>
              ))}
            </div>
          )}
        </ContentCard>

        <ContentCard>
          <h3 style={{marginBottom:'1.5rem'}}>{t('portal.teacher.payments.title_history', 'Betalingsgeschiedenis')} ({payments.length})</h3>
          {loading ? (
            <div style={{ padding:'1rem', color:'#555' }}>{t('portal.teacher.payments.loading_history', 'Loading payments…')}</div>
          ) : error ? (
            <div style={{ padding:'1rem', color:'#b00020' }}>{error}</div>
          ) : payments.length === 0 ? (
            <div style={{ padding:'1.25rem', textAlign:'center', color:'#666' }}>
              <div style={{ fontSize:'2rem', opacity:.35, marginBottom:8 }}>💰</div>
              {t('portal.teacher.payments.no_history', 'No payments to show.')}
            </div>
          ) : (
            <div>
              {payments.map((p:any)=> (
                <ListItem key={p.id}>
                  <ItemLeft>
                    <span className="icon">💳</span>
                    <div className="details">
                      <div className="title">{titleFor(p)}</div>
                      <div className="meta">
                        {p.method || 'BANK_TRANSFER'} • 
                        <span>
                          {new Date(p.createdAt || Date.now()).toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : (i18n.language === 'tr' ? 'tr-TR' : 'en-GB'))}
                        </span>
                        <span className="time-part">
                          {' '}{new Date(p.createdAt || Date.now()).toLocaleTimeString(i18n.language === 'nl' ? 'nl-NL' : (i18n.language === 'tr' ? 'tr-TR' : 'en-GB'), { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  </ItemLeft>
                  <ItemRight>
                    <div className="actions">
                      {p.status === 'PENDING' && (
                        <>
                          <button 
                            disabled={actionLoading === p.id}
                            onClick={() => handleVerify(p.id, 'VERIFIED')}
                            style={{ 
                              border: 'none', 
                              borderRadius: 10, 
                              padding: '.3rem .6rem', 
                              fontWeight: 800, 
                              color: '#fff', 
                              background: '#10b981',
                              cursor: 'pointer',
                              fontSize: '.75rem',
                            }}
                          >
                            {actionLoading === p.id ? '...' : t('portal.common.approve', 'Approve')}
                          </button>
                          <button 
                            disabled={actionLoading === p.id}
                            onClick={() => handleVerify(p.id, 'REJECTED')}
                            style={{ 
                              border: 'none', 
                              borderRadius: 10, 
                              padding: '.3rem .6rem', 
                              fontWeight: 800, 
                              color: '#fff', 
                              background: '#ef4444',
                              cursor: 'pointer',
                              fontSize: '.75rem',
                            }}
                          >
                            {actionLoading === p.id ? '...' : t('portal.common.reject', 'Reject')}
                          </button>
                        </>
                      )}
                      <button 
                        onClick={() => navigate(`/portal/teacher/student/${p.studentId || p.student?.id}`)}
                        style={{ 
                          border: '2px solid #667eea', 
                          borderRadius: 10, 
                          padding: '.3rem .6rem', 
                          fontWeight: 800, 
                          color: '#667eea', 
                          background: 'transparent',
                          cursor: 'pointer',
                          fontSize: '.75rem',
                        }}
                      >
                        {t('portal.common.details', 'Details')}
                      </button>
                    </div>
                    <div className="amount history">{formatCurrency(p.amount || 0, p.currency || 'EUR')}</div>
                    <Badge color={statusColor(p.status)}>{String(p.status || '').toUpperCase() || 'PENDING'}</Badge>
                  </ItemRight>
                </ListItem>
              ))}
            </div>
          )}
        </ContentCard>
      </ContentWrapper>
    </PageWrapper>
  );
};

export default TeacherPayments;
